import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type SearchClickStats = {
  today: number;
  yesterday: number;
};

const LAST_KEY = "tl_stats_last";
const FREEZE_KEY = "tl_stats_freeze_until";
const FREEZE_MS = 5 * 60 * 1000;

function parseStats(value: unknown): SearchClickStats | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;

  const stats = value as Record<string, unknown>;
  if (typeof stats.today !== "number" || typeof stats.yesterday !== "number") return null;

  return { today: stats.today, yesterday: stats.yesterday };
}

function readLast(): SearchClickStats | null {
  try {
    return parseStats(JSON.parse(localStorage.getItem(LAST_KEY) || "null"));
  } catch {
    return null;
  }
}

function readFreezeUntil(): number {
  try {
    return Number(localStorage.getItem(FREEZE_KEY)) || 0;
  } catch {
    return 0;
  }
}

/**
 * Wywoływane przez wyszukiwarkę przy policzonym wyszukiwaniu: licznik
 * zamraża pokazywane liczby na 5 minut (także po przeładowaniu strony).
 */
export function freezeSearchClickStats() {
  try {
    localStorage.setItem(FREEZE_KEY, String(Date.now() + FREEZE_MS));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent("tl:search-counted"));
}

// „Dziś kupiono 1 bilet / 2 bilety / 5 biletów" (11 i 21 biletów, 22 bilety).
function biletNoun(n: number): string {
  const abs = Math.abs(n);
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (abs === 1) return "bilet";
  if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) return "bilety";
  return "biletów";
}

export function SearchClickStats() {
  const [stats, setStats] = useState<SearchClickStats | null>(null);

  useEffect(() => {
    let active = true;
    let timer: number | undefined;

    const loadStats = async () => {
      const { data, error } = await supabase.rpc("get_search_click_stats");
      if (!active || error) return;

      const nextStats = parseStats(data);
      if (!nextStats) return;
      setStats(nextStats);
      try {
        localStorage.setItem(LAST_KEY, JSON.stringify(nextStats));
      } catch {
        /* ignore */
      }
    };

    // Liczby odświeżają się dopiero 5 minut po wyszukiwaniu użytkownika —
    // do tego czasu pokazujemy ostatnio widziane (także po przeładowaniu strony).
    const sync = () => {
      window.clearTimeout(timer);
      const wait = readFreezeUntil() - Date.now();
      const last = readLast();
      if (wait > 0 && last) {
        setStats(last);
        timer = window.setTimeout(loadStats, wait);
      } else {
        void loadStats();
      }
    };

    sync();
    window.addEventListener("tl:search-counted", sync);
    // Dodatkowo liczby odświeżają się same co 5 minut, bez przeładowania strony
    // (sync respektuje okno zamrożenia po wyszukiwaniu użytkownika).
    const interval = window.setInterval(sync, FREEZE_MS);

    return () => {
      active = false;
      window.clearTimeout(timer);
      window.clearInterval(interval);
      window.removeEventListener("tl:search-counted", sync);
    };
  }, []);

  if (!stats) return null;

  return (
    <p className="mt-3 text-center text-sm font-medium text-foreground/80" aria-live="polite">
      Dziś kupiono {stats.today} {biletNoun(stats.today)} <span aria-hidden="true">·</span>{" "}
      wczoraj: {stats.yesterday}
    </p>
  );
}
